/**
 * Readable messages out of a failed React Query / graphql-request call.
 *
 * graphql-request's `ClientError` keeps the server errors on `response.errors` and stuffs the WHOLE
 * request/response JSON into `error.message`. Any handler that falls back to `error.message` therefore
 * shows an operator a serialised GraphQL payload — which is exactly what NRG saw in a toast when
 * Member Movement was switched off for their brand (OC-1461).
 *
 * Lookup order: server `extensions.userMessage` → server `message` → the message of an error that
 * carries no `response` at all (a plain `Error`, e.g. a mapper throw) → the caller's fallback. An
 * error that HAS a response but no usable server message falls through to the fallback rather than
 * to `error.message`, because for that shape `error.message` is the blob.
 *
 * Promoted here from `pages/schedule/.../graphql-error.utils.ts` so every call site shares one
 * implementation; `getRequestErrorReason` is the new half, reading the stable machine code the
 * backend puts on `extensions.reason`.
 */

type ServerError = {
  message?: string
  extensions?: { userMessage?: string; reason?: string; code?: string }
}

type RequestErrorShape = {
  message?: string
  response?: {
    errors?: ReadonlyArray<ServerError>
  }
  graphQLErrors?: ReadonlyArray<ServerError>
  extensions?: ServerError['extensions']
}

export const getRequestErrorMessage = (error: unknown, fallback: string): string => {
  const shaped = error as RequestErrorShape | null | undefined
  const serverError = shaped?.response?.errors?.[0]
  if (serverError?.extensions?.userMessage) {
    return serverError.extensions.userMessage
  }
  if (serverError?.message) {
    return serverError.message
  }
  if (shaped?.response === undefined && shaped?.message) {
    return shaped.message
  }
  return fallback
}

/** The backend's machine code for the failure, when it set one. */
export const getRequestErrorReason = (error: unknown): string | null => {
  const shaped = error as RequestErrorShape | null | undefined
  return shaped?.response?.errors?.[0]?.extensions?.reason ?? null
}

export const getRequestErrorCode = (error: unknown): string | null => {
  const shaped = error as RequestErrorShape | null | undefined
  const serverError = shaped?.response?.errors?.[0] ?? shaped?.graphQLErrors?.[0]
  return serverError?.extensions?.code ?? shaped?.extensions?.code ?? null
}
